interface Route {
  route: string;
  priority: number;
  test: (pathname: string) => boolean;
  getPathParams: (
    pathname: string,
  ) => Record<string, string | Array<string>> | null;
}

interface RouteState {
  route: string;
  priority: number;
  queryKeys: Array<string>;
  pattern: Array<string>;
  isRegExp?: boolean;
  status?: "stop";
  isRestOptional?: true;
  restPaths?: string;
}

const HARDCODED_ROUTE_PRIORITY = 300;
const SIMPLE_PARAMS_ROUTE_PRIORITY = 200;
const MANDATORY_REST_PARAM_ROUTE_PRIOTITY = 100;
const OPTIONAL_REST_PARAM_ROUTE_PRIORITY = 0;
const MAX_PRIORITY = Math.max(
  HARDCODED_ROUTE_PRIORITY,
  SIMPLE_PARAMS_ROUTE_PRIORITY,
  MANDATORY_REST_PARAM_ROUTE_PRIOTITY,
  OPTIONAL_REST_PARAM_ROUTE_PRIORITY,
);

const isRawChunk = (chunk: string) => /^[\w_-]+$/.test(chunk);
const isSimpleParam = (chunk: string) => /^\[[\w_-]+]$/.test(chunk);
const isListParam = (chunk: string) => /^\[\.\.\.[\w_-]+]$/.test(chunk);
const isOptionalListParam = (chunk: string) =>
  /^\[\[\.\.\.[\w_-]+]]$/.test(chunk);

const parseChunk = (state: RouteState, chunk: string): RouteState => {
  if (state.restPaths) {
    throw new Error(
      `In route "${state.route}": chunk "${chunk}" cannot be parsed after parsing rest of paths chunk "...${state.restPaths}"`,
    );
  } else if (state.status === "stop") {
    throw new Error(
      `In route "${state.route}": a path with multiple slashes is not valid`,
    );
  } else if (isRawChunk(chunk)) {
    state.pattern.push(chunk);
    state.priority = Math.min(state.priority, HARDCODED_ROUTE_PRIORITY);
    return state;
  } else if (isSimpleParam(chunk)) {
    state.pattern.push("([^/\\s]+)");
    state.queryKeys.push(chunk.slice(1, -1));
    state.priority = Math.min(state.priority, SIMPLE_PARAMS_ROUTE_PRIORITY);
    state.isRegExp = true;
    return state;
  } else if (isListParam(chunk)) {
    state.pattern.push("((?:[^/\\s]+\\/)*(?:[^/\\s]+)\\/?)");
    state.restPaths = chunk.slice(4, -1);
    state.priority = Math.min(
      state.priority,
      MANDATORY_REST_PARAM_ROUTE_PRIOTITY,
    );
    state.isRegExp = true;
    return state;
  } else if (isOptionalListParam(chunk)) {
    state.pattern.push("((?:[^/\\s]+\\/)*(?:[^/\\s]+)?)");
    state.restPaths = chunk.slice(5, -2);
    state.isRestOptional = true;
    state.priority = Math.min(
      state.priority,
      OPTIONAL_REST_PARAM_ROUTE_PRIORITY,
    );
    state.isRegExp = true;
    return state;
  } else if (chunk === "") {
    state.pattern.push(chunk);
    state.status = "stop";
    return state;
  }
  throw new Error(
    `In route "${state.route}": "${chunk}" is not a valid part of the URL"`,
  );
};

const addMandatorySlashes = (pattern: Array<string>) => pattern.join("\\/");

const createPattern = ({ pattern, isRestOptional }: RouteState) => {
  if (!isRestOptional) {
    return new RegExp(`^${addMandatorySlashes(pattern)}\\/?$`);
  }
  const optionalRestParam = pattern[pattern.length - 1];
  return new RegExp(
    `^${addMandatorySlashes(pattern.slice(0, -1))}(?:\\/${optionalRestParam})?`,
  );
};

const createPathParams = (state: RouteState, matches: Array<string>) => {
  const params = matches.slice(1, state.queryKeys.length + 1).reduce(
    (query, match, index) => {
      query[state.queryKeys[index]] = match;
      return query;
    },
    {} as Record<string, string | Array<string>>,
  );
  if (state.restPaths) {
    const pathParams = matches[state.queryKeys.length + 1];
    if (pathParams) {
      params[state.restPaths] = pathParams.replace(/\/$/, "").split("/");
    }
  }
  return params;
};

const trimmedPath = (path: string) => path.replace(/\/+$/, "");
const createRoute = (state: RouteState) => {
  if (!state.isRegExp) {
    const pattern = trimmedPath(state.pattern.join("/"));
    const test = (pathname: string) => pattern === trimmedPath(pathname);
    return {
      priority: state.priority,
      route: state.route,
      test,
      getPathParams: (pathname: string) => (test(pathname) ? {} : null),
    };
  }
  const pattern = createPattern(state);
  return {
    priority: state.priority,
    route: state.route,
    test: (pathname: string) => pattern.test(pathname),
    getPathParams: (pathname: string) => {
      const matches = pattern.exec(pathname);
      if (!matches) {
        return null;
      }
      return createPathParams(state, matches);
    },
  };
};

const endsWithSlash = (chunks: Array<string>) =>
  chunks.at(-1) === "" && chunks.at(-2) !== "";

export const parseRoute = (route: string): Route => {
  if (route.startsWith("/")) {
    throw new Error(`In route "${route}": route must not start with slash`);
  }
  const chunks = route.split("/");
  if (endsWithSlash(chunks)) {
    chunks.pop();
  }
  const state = chunks.reduce(parseChunk, {
    pattern: [],
    route,
    priority: MAX_PRIORITY,
    queryKeys: [],
  });
  return createRoute(state);
};
